import {
  useEffect,
  useEffectEvent,
  useId,
  useMemo,
  useState,
  type InputHTMLAttributes,
} from 'react';
import { type Column, type RowData, type Table } from '@tanstack/react-table';
import { FaTimes } from 'react-icons/fa';
import type { AppTableFeatures } from './tableFeatures';

type DebouncedInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'defaultValue' | 'onChange'
> & {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
};

export function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 300,
  type = 'search',
  className = '',
  ...props
}: DebouncedInputProps) {
  const externalValue = String(initialValue);
  const [draft, setDraft] = useState({
    value: externalValue,
    externalValue,
    dirty: false,
  });
  const commit = useEffectEvent(onChange);

  // External updates (including resets) replace edits without emitting a change.
  if (draft.externalValue !== externalValue) {
    setDraft({ value: externalValue, externalValue, dirty: false });
  }

  useEffect(() => {
    if (!draft.dirty || draft.value === draft.externalValue) return;
    const timeout = setTimeout(() => {
      setDraft((current) => ({ ...current, dirty: false }));
      commit(draft.value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [draft, debounce]);

  return (
    <div className='flex w-full min-w-0 items-center gap-1'>
      <input
        {...props}
        type={type}
        value={draft.value}
        onChange={(event) =>
          setDraft({
            value: event.target.value,
            externalValue,
            dirty: true,
          })
        }
        className={`bg-gray-50 block min-w-0 flex-1 rounded-lg border border-gray-300 p-2.5
          text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500
          dark:border-0 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400
          dark:focus:ring-gray-400 ${className}`}
      />
      {draft.value !== '' && (
        <button
          type='button'
          aria-label={`Clear ${props['aria-label'] ?? 'filter'}`}
          onClick={() => {
            setDraft({ value: '', externalValue, dirty: false });
            commit('');
          }}
          className='absolute right-4 flex p-1  shrink-0 items-center justify-center rounded-full text-rose-600 hover:text-rose-700 dark:text-rose-400 dark:hover:text-rose-500  bg-gray-200  focus:outline-none focus:ring-2 focus:ring-blue-500  dark:bg-gray-600 '
        >
          <FaTimes aria-hidden='true' />
        </button>
      )}
    </div>
  );
}

export function Filter<T extends RowData>({
  column,
  table,
}: {
  column: Column<AppTableFeatures, T, unknown>;
  table: Table<AppTableFeatures, T>;
}) {
  const id = useId();
  const listId = `${id}-suggestions`;
  const firstRowWithValue = table
    .getPreFilteredRowModel()
    .flatRows.find((row) => {
      const value = row.getValue(column.id);
      return value !== null && value !== undefined;
    });
  const isNumber = typeof firstRowWithValue?.getValue(column.id) === 'number';
  const columnFilterValue = column.getFilterValue();
  const header = column.columnDef.header;
  const label = `Filter ${typeof header === 'string' && header ? header : column.id}`;
  const facetedValues = column.getFacetedUniqueValues();

  const sortedUniqueValues = useMemo(
    () =>
      isNumber
        ? []
        : Array.from(facetedValues.keys())
            .filter(
              (value): value is string =>
                typeof value === 'string' && value !== '',
            )
            .sort((a, b) => a.localeCompare(b)),
    [facetedValues, isNumber],
  );

  return isNumber ? (
    <DebouncedInput
      type='number'
      step='any'
      id={id}
      aria-label={label}
      value={typeof columnFilterValue === 'number' ? columnFilterValue : ''}
      onChange={(value) =>
        column.setFilterValue(value === '' ? undefined : Number(value))
      }
      placeholder='Exact number'
      className='shadow-sm'
    />
  ) : (
    <>
      <datalist id={listId}>
        {sortedUniqueValues.slice(0, 5000).map((value) => (
          <option value={value} key={value} aria-label={value} />
        ))}
      </datalist>
      <DebouncedInput
        type='text'
        id={id}
        aria-label={label}
        value={typeof columnFilterValue === 'string' ? columnFilterValue : ''}
        onChange={(value) =>
          column.setFilterValue(value === '' ? undefined : value)
        }
        placeholder={`Search (${sortedUniqueValues.length})`}
        className='shadow-sm'
        list={listId}
      />
    </>
  );
}
